import {decodePKCS12} from './helper'
import React, {useCallback, useState} from 'react'
import {
  PlatformAppleCertificate,
  PlatformAppleToken,
  PlatformAttributes
} from '../../../common/types'
import {Select, TextInput} from '../../../components/library/form'
import {
  FileDrop,
  FilePositioner,
  FilePreview,
  onDropRejectedHandler
} from '../../../components/library/form/file-drop'
import {useToast} from '../../../lib/toast'
import {PlatformAuthenticationType} from './types'
import {WORKBENCH_MAX_UPLOAD_FILE_SIZE_BYTES} from '../../../common/types/constants'

const authTypes: {type: PlatformAuthenticationType; displayValue: string}[] = [
  {type: 'certificate', displayValue: 'Certificate'},
  {type: 'token', displayValue: 'Token'}
]
const currentAuthState = (
  attributes: PlatformAttributes
): PlatformAuthenticationType => {
  // not able to find a way to check the type
  const checkTokenType = attributes as PlatformAppleToken
  if (
    checkTokenType.bundleIdentifier ||
    checkTokenType.signingKey ||
    checkTokenType.signingKeyId ||
    checkTokenType.teamIdentifier
  ) {
    return 'token'
  }
  return 'certificate'
}
export const IosForm = ({
  attributes,
  setAttributes
}: {
  attributes: PlatformAttributes
  setAttributes: (
    setter:
      | Partial<PlatformAttributes>
      | ((previous: Partial<PlatformAttributes>) => Partial<PlatformAttributes>)
  ) => void
}) => {
  const [authType, setAuthType] = useState<
    PlatformAuthenticationType | undefined
  >(currentAuthState(attributes))
  const uploadFieldHeight = '200px'

  return (
    <>
      <div style={{marginBottom: '20px'}}>
        <Select
          label="Select type..."
          upLabel="Authentication type"
          value={authType}
          options={authTypes.map(item => ({
            text: item.displayValue,
            value: item.type
          }))}
          onChange={(value: PlatformAuthenticationType | undefined) => {
            setAuthType(value)
          }}
        />
      </div>

      {authType === 'token' ? (
        <IosTokenForm
          attributes={attributes as PlatformAppleToken}
          setAttributes={setAttributes}
          uploadFieldHeight={uploadFieldHeight}
        ></IosTokenForm>
      ) : (
        <IosCertificateForm
          attributes={attributes as PlatformAppleCertificate}
          setAttributes={setAttributes}
          uploadFieldHeight={uploadFieldHeight}
        />
      )}
    </>
  )
}

const IosTokenForm = ({
  attributes,
  setAttributes,
  uploadFieldHeight
}: {
  attributes: PlatformAppleToken
  setAttributes: (
    setter:
      | Partial<PlatformAttributes>
      | ((previous: Partial<PlatformAttributes>) => Partial<PlatformAttributes>)
  ) => void
  uploadFieldHeight: string
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const haveFile = attributes.signingKey
  const toaster = useToast()

  const onDropTokenFile = useCallback(
    async (files: File[]) => {
      setIsLoading(true)
      if (!files || !files.length) {
        toaster('No file supplied', 'error')
      }
      return files[0]
        .text()
        .then(value => {
          setAttributes((attr: Partial<PlatformAttributes>) => {
            return attr
              ? {
                  ...attr,
                  signingKey: value
                }
              : attr
          })
          setIsLoading(false)
        })
        .catch(e => {
          if (e instanceof Error) {
            toaster(`Unable to parse key (${e.message})`, 'error')
          } else {
            toaster(`Unable to parse key`, 'error')
          }
        })
    },
    [setIsLoading, setAttributes, toaster]
  )

  return (
    <>
      <TextInput
        style={{marginBottom: '20px'}}
        label="Team identifier..."
        upLabel="Team identifier"
        type="text"
        value={attributes.teamIdentifier ?? ''}
        onChange={value => {
          setAttributes(editing => {
            return editing
              ? {
                  ...editing,
                  teamIdentifier: value
                }
              : editing
          })
        }}
      />
      <TextInput
        style={{marginBottom: '20px'}}
        label="Bundle identifier..."
        upLabel="Bundle identifier"
        type="text"
        value={attributes.bundleIdentifier ?? ''}
        onChange={value => {
          setAttributes(attr => {
            return attr
              ? {
                  ...attributes,
                  bundleIdentifier: value
                }
              : attr
          })
        }}
      />
      <TextInput
        style={{marginBottom: '20px'}}
        label="Signing key ID..."
        upLabel="Signing key ID"
        type="text"
        value={attributes.signingKeyId ?? ''}
        onChange={value => {
          setAttributes(attr => {
            return attr
              ? {
                  ...attr,
                  signingKeyId: value
                }
              : attr
          })
        }}
      />

      <FilePositioner>
        <FileDrop
          height={uploadFieldHeight}
          isLoading={isLoading}
          hasPreview={!!haveFile}
          desc="Select manually or drag P8 signing key here"
          drop="Drop to upload signing key"
          success="Key attached"
          onDrop={onDropTokenFile}
          dropzoneProps={{
            maxSize: WORKBENCH_MAX_UPLOAD_FILE_SIZE_BYTES,
            onDropRejected: onDropRejectedHandler(
              toaster,
              WORKBENCH_MAX_UPLOAD_FILE_SIZE_BYTES
            )
          }}
        />
        {haveFile && <FilePreview height={uploadFieldHeight}></FilePreview>}
      </FilePositioner>
    </>
  )
}

const IosCertificateForm = ({
  attributes,
  setAttributes,
  uploadFieldHeight
}: {
  attributes: PlatformAppleCertificate
  setAttributes: (
    setter:
      | Partial<PlatformAttributes>
      | ((previous: Partial<PlatformAttributes>) => Partial<PlatformAttributes>)
  ) => void
  uploadFieldHeight: string
}) => {
  const [password, setPassword] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const toaster = useToast()

  const onDrop = useCallback(
    async (files: File[]) => {
      setIsLoading(true)
      let decoded: undefined | {certificate: string; privateKey: string}
      try {
        decoded = await decodePKCS12(
          files[0],
          password === '' ? undefined : password
        )
      } catch (e) {
        if (e instanceof Error) {
          toaster(`Unable to parse certificate (${e.message})`, 'error')
        } else {
          toaster(`Unable to parse certificate`, 'error')
        }
      }
      if (decoded) {
        setAttributes(attr => {
          return attr
            ? {
                ...attr,
                sslCertificate: decoded?.certificate ?? '',
                privateKey: decoded?.privateKey ?? ''
              }
            : attr
        })
      }
      setIsLoading(false)
    },
    [setAttributes, toaster, password]
  )

  const haveFile =
    attributes && attributes.sslCertificate && attributes.privateKey

  return (
    <>
      <TextInput
        style={{marginBottom: '20px'}}
        label="Add certificate password if applicable..."
        upLabel="Certificate password"
        type="password"
        value={password ?? ''}
        autoComplete="new-password"
        onChange={setPassword}
      />
      <FilePositioner>
        <FileDrop
          height={uploadFieldHeight}
          isLoading={isLoading}
          hasPreview={!!haveFile}
          desc="Select manually or drag P12 certificate here"
          drop="Drop to upload certificate"
          success="Certificate attached"
          onDrop={onDrop}
          dropzoneProps={{
            maxSize: WORKBENCH_MAX_UPLOAD_FILE_SIZE_BYTES,
            onDropRejected: onDropRejectedHandler(
              toaster,
              WORKBENCH_MAX_UPLOAD_FILE_SIZE_BYTES
            )
          }}
        />
        {haveFile && <FilePreview height={uploadFieldHeight}></FilePreview>}
      </FilePositioner>
    </>
  )
}
